/**
 * useDashboardUpdate — Hook for triggering and tracking a dashboard update.
 *
 * State machine:
 *   idle -> starting -> streaming -> reconnecting -> verifying -> done | version_unchanged | failed
 *
 * The update restarts the service, so the SSE stream will disconnect.
 * Recovery is handled via exponential-backoff polling of /health.
 *
 * Hardening (2026-03):
 *   - SSE error = immediate recovery: during update, any disconnect triggers recovery polling (no tolerance)
 *   - SSE silence timeout: 30s of no events in streaming phase triggers recovery polling
 *   - Job poll transitions: "succeeded" closes SSE + starts recovery; "failed" closes SSE + errors
 *   - Poll progress: logs attempt count periodically so user sees active recovery
 *   - Dev channel awareness: "version_unchanged" with isReinstall flag for dev/beta channels
 *   - 401 handling: both job and meta 401 now cleanly resolve to "done + reload"
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

import type { DashboardChannel } from "./useSystemMeta";

export type UpdatePhase =
  | "idle"
  | "starting"
  | "streaming"
  | "reconnecting"
  | "verifying"
  | "done"
  | "version_unchanged"
  | "failed";

type UpdateState = {
  phase: UpdatePhase;
  lines: string[];
  error: string | null;
  newCommit: string | null;
  newVersion: string | null;
  /** True when version_unchanged is a dev/beta channel reinstall, not a failure. */
  isReinstall: boolean;
  /**
   * Non-null when the backend escalated to a full `qb update quickbox` (the CLI
   * core is also behind). Carries the maintenance jobId so the caller can attach
   * the maintenance modal to the already-running job instead of the dashboard-only
   * stream. Null on the normal dashboard-only path.
   */
  escalatedJobId: string | null;
};

const HEALTH_URL = "/health";
const META_URL = "/api/system/meta";
const TRIGGER_URL = "/api/admin/system/dashboard-update";
const STREAM_URL = "/api/admin/system/dashboard-update/stream";
const JOB_URL = "/api/admin/system/dashboard-update/job";

const POLL_BACKOFF = [500, 1000, 2000, 3000, 5000] as const;
const MAX_POLL_ATTEMPTS = 120; // ~10 minutes at max backoff
const JOB_POLL_INTERVAL = 3000; // Poll job status every 3 seconds as fallback
/** Log poll progress every N attempts. */
const POLL_PROGRESS_INTERVAL = 10;
/**
 * If no SSE event arrives within this window (ms) during the streaming phase,
 * assume the connection is stale and switch to recovery polling.
 * 30 seconds is generous — build/restart silences are normal, but permanent
 * silence means the server stopped and we missed the disconnect.
 */
const SSE_SILENCE_TIMEOUT_MS = 30_000;

type UseDashboardUpdateOptions = {
  currentCommitShort: string | null;
  currentVersion: string | null;
  channel?: DashboardChannel | null;
};

export const useDashboardUpdate = (options: UseDashboardUpdateOptions) => {
  const { currentCommitShort, currentVersion, channel } = options;

  const [state, setState] = useState<UpdateState>({
    phase: "idle",
    lines: [],
    error: null,
    newCommit: null,
    newVersion: null,
    isReinstall: false,
    escalatedJobId: null,
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptRef = useRef(0);
  const phaseRef = useRef<UpdatePhase>("idle");
  const mountedRef = useRef(true);
  const jobIdRef = useRef<string | null>(null);
  /** Timer that fires when no SSE event has arrived for SSE_SILENCE_TIMEOUT_MS. */
  const sseSilenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  phaseRef.current = state.phase;

  const isPreReleaseChannel = channel === "dev" || channel === "beta";

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      eventSourceRef.current?.close();
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (jobPollTimerRef.current) clearInterval(jobPollTimerRef.current);
      if (sseSilenceTimerRef.current) clearTimeout(sseSilenceTimerRef.current);
    };
  }, []);

  const addLine = useCallback((text: string) => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, lines: [...prev.lines, text] }));
  }, []);

  const setPhase = useCallback((phase: UpdatePhase) => {
    if (!mountedRef.current) return;
    phaseRef.current = phase;
    setState((prev) => ({ ...prev, phase }));
  }, []);

  const setError = useCallback((error: string) => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, phase: "failed", error }));
    phaseRef.current = "failed";
  }, []);

  // ── Recovery polling ──────────────────────────────────────────────────

  const startRecoveryPolling = useCallback(() => {
    setPhase("reconnecting");
    pollAttemptRef.current = 0;
    addLine("[info] Service is restarting. Waiting for recovery...");

    const poll = async () => {
      if (!mountedRef.current) return;
      // Continue polling in both reconnecting and verifying phases
      if (phaseRef.current !== "reconnecting" && phaseRef.current !== "verifying") return;

      pollAttemptRef.current += 1;
      const attempt = pollAttemptRef.current;

      if (attempt > MAX_POLL_ATTEMPTS) {
        setError("Timed out waiting for service to restart. Check server logs.");
        return;
      }

      // Periodic progress feedback so user knows polling is active
      if (attempt > 1 && attempt % POLL_PROGRESS_INTERVAL === 0) {
        const elapsed = Math.round(attempt * 5); // rough seconds (max backoff is 5s)
        addLine(`[info] Still waiting for service... (attempt ${attempt}, ~${elapsed}s elapsed)`);
      }

      try {
        const res = await fetch(HEALTH_URL, { cache: "no-store" });
        if (res.ok) {
          // Health check passed -- verify the update
          if (!mountedRef.current) return;

          // Only set phase to verifying if we're not already there
          if (phaseRef.current === "reconnecting") {
            setPhase("verifying");
            addLine("[info] Service is back online. Verifying update...");
          }

          // ── PRIMARY: Server-side job verification ──────────────────
          // The backend runs finalizePendingUpdateJob() at startup which
          // sets versionVerified in the job state. Check this first.
          const jobResult = await tryJobVerification();
          if (jobResult !== "fallthrough") return;

          if (!mountedRef.current) return;

          // ── FALLBACK: Meta endpoint check with retry ────────────────
          await tryMetaVerification();
          return;
        }
      } catch {
        // Health check failed -- keep polling
      }

      scheduleNextPoll();
    };

    // ── Job endpoint verification (primary path) ──────────────────────
    const tryJobVerification = async (): Promise<"resolved" | "fallthrough"> => {
      try {
        const jobRes = await fetch(JOB_URL, { credentials: "include", cache: "no-store" });

        if (jobRes.status === 401) {
          // Session expired after restart -- fall through to meta check which
          // has its own 401 handling. This is expected for non-remember-me sessions.
          return "fallthrough";
        }

        if (jobRes.ok) {
          const jobData = await jobRes.json() as {
            ok: boolean;
            job?: {
              versionVerified?: boolean | null;
              postUpdateVersion?: string | null;
              postUpdateCommit?: string | null;
            };
          };
          if (!mountedRef.current) return "resolved";

          if (jobData.ok && jobData.job?.versionVerified === true) {
            const jobVersion = jobData.job.postUpdateVersion ?? null;
            const jobCommit = jobData.job.postUpdateCommit ?? null;
            const display = jobVersion && jobVersion !== "unknown" ? `v${jobVersion}` : jobCommit ?? "unknown";
            addLine(`[info] Update verified by server. Current: ${display}`);
            setState((prev) => ({
              ...prev,
              phase: "done",
              newCommit: jobCommit,
              newVersion: jobVersion,
              isReinstall: false,
            }));
            phaseRef.current = "done";
            return "resolved";
          }

          if (jobData.ok && jobData.job?.versionVerified === false) {
            const jobCommit = jobData.job.postUpdateCommit ?? null;
            const jobVersion = jobData.job.postUpdateVersion ?? null;

            if (isPreReleaseChannel) {
              addLine(`[info] Version unchanged after update (${channel} channel).`);
              addLine("[info] This is normal when reinstalling the same pre-release version.");
            } else {
              addLine("[warn] Server verified the update but version did not change.");
              addLine("[warn] The update may not have been applied. Check server logs for details.");
            }

            setState((prev) => ({
              ...prev,
              phase: "version_unchanged",
              newCommit: jobCommit,
              newVersion: jobVersion,
              isReinstall: isPreReleaseChannel,
            }));
            phaseRef.current = "version_unchanged";
            return "resolved";
          }

          // versionVerified === null -- not yet determined, fall through to meta
        }
      } catch { /* Job endpoint unavailable -- fall through to meta check */ }
      return "fallthrough";
    };

    // ── Meta endpoint verification (fallback path) ────────────────────
    const tryMetaVerification = async () => {
      // Retry up to 3 times with 2s delay between attempts to give
      // the server time to finish startup and Central to respond.
      let metaRes: Response | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (!mountedRef.current) return;
        try {
          metaRes = await fetch(META_URL, { credentials: "include", cache: "no-store" });
          if (metaRes.ok) break;
          // Non-OK but not worth retrying (e.g., 401)
          if (metaRes.status === 401) break;
        } catch {
          // Network error -- retry
          metaRes = null;
        }
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 2000));
        }
      }

      if (!mountedRef.current) return;

      try {
        if (metaRes?.ok) {
          const metaData = await metaRes.json() as Record<string, unknown>;
          const dv = metaData.dashboardVersion as Record<string, unknown> | undefined;
          const newShort = (dv?.currentCommitShort as string) ?? null;
          const newVersion = (dv?.currentVersion as string) ?? null;
          const status = (dv?.status as string) ?? null;

          if (!mountedRef.current) return;

          const displayVersion = newVersion && newVersion !== "unknown" ? `v${newVersion}` : newShort ?? "unknown";

          // Multi-signal verification: any positive signal means success
          const commitChanged = newShort != null && newShort !== currentCommitShort;
          const versionStringChanged =
            newVersion != null && newVersion !== "unknown" &&
            currentVersion != null && currentVersion !== "unknown" &&
            newVersion !== currentVersion;
          const centralConfirmed = status === "up_to_date";

          if (commitChanged || versionStringChanged || centralConfirmed) {
            addLine(`[info] Update complete. Current: ${displayVersion}`);
            setState((prev) => ({
              ...prev,
              phase: "done",
              newCommit: newShort,
              newVersion,
              isReinstall: false,
            }));
            phaseRef.current = "done";
          } else if (status === "unavailable" || status === "not_configured" || status === null) {
            // Central unreachable and job verification was null -- no definitive signal
            if (isPreReleaseChannel) {
              addLine(`[info] Version unchanged (${displayVersion}). This is expected on the ${channel} channel when the latest version is already installed.`);
            } else {
              addLine(`[warn] Update completed but version unchanged (${displayVersion}).`);
              addLine("[warn] Central was unreachable, so update status could not be confirmed.");
            }
            setState((prev) => ({
              ...prev,
              phase: "version_unchanged",
              newCommit: newShort,
              newVersion,
              isReinstall: isPreReleaseChannel,
            }));
            phaseRef.current = "version_unchanged";
          } else {
            // Central reachable but says update_available -- genuinely unchanged
            if (isPreReleaseChannel) {
              addLine(`[info] Version unchanged (${displayVersion}). Pre-release reinstall completed.`);
            } else {
              addLine(`[warn] Update completed but version unchanged (${displayVersion}).`);
              addLine("[warn] The update may not have been applied. Check server logs for details.");
            }
            setState((prev) => ({
              ...prev,
              phase: "version_unchanged",
              newCommit: newShort,
              newVersion,
              isReinstall: isPreReleaseChannel,
            }));
            phaseRef.current = "version_unchanged";
          }
        } else if (metaRes?.status === 401) {
          // Session expired after restart - this is expected
          // Service is up (health passed), just need to reload to re-auth
          if (!mountedRef.current) return;
          addLine("[info] Service restarted successfully. Please reload to re-authenticate.");
          setState((prev) => ({
            ...prev,
            phase: "done",
            newCommit: null,
            isReinstall: false,
          }));
          phaseRef.current = "done";
        } else {
          // Meta endpoint not ready after retries, keep polling
          scheduleNextPoll();
        }
      } catch {
        // Parse error on meta response -- keep polling
        scheduleNextPoll();
      }
    };

    const scheduleNextPoll = () => {
      if (!mountedRef.current) return;
      if (phaseRef.current !== "reconnecting" && phaseRef.current !== "verifying") return;

      const idx = Math.min(pollAttemptRef.current, POLL_BACKOFF.length - 1);
      const delay = POLL_BACKOFF[idx]!;
      pollTimerRef.current = setTimeout(() => void poll(), delay);
    };

    // Start first poll after a brief delay (service needs time to stop)
    pollTimerRef.current = setTimeout(() => void poll(), 2000);
  }, [addLine, setPhase, setError, currentCommitShort, currentVersion, isPreReleaseChannel, channel]);

  // ── Job status polling (fallback for SSE) ─────────────────────────────
  // Runs in parallel with SSE to catch job state changes even if SSE fails.
  // This is especially important when the service restarts during update.

  const stopJobPolling = useCallback(() => {
    if (jobPollTimerRef.current) {
      clearInterval(jobPollTimerRef.current);
      jobPollTimerRef.current = null;
    }
  }, []);

  const startJobPolling = useCallback(() => {
    stopJobPolling();

    const pollJob = async () => {
      if (!mountedRef.current) return;
      // Only poll while streaming or reconnecting
      if (phaseRef.current !== "streaming" && phaseRef.current !== "reconnecting") {
        stopJobPolling();
        return;
      }

      try {
        const res = await fetch(JOB_URL, { credentials: "include", cache: "no-store" });
        if (!res.ok) return;

        const data = await res.json() as { ok: boolean; job?: { status: string; error?: string } };
        if (!data.ok || !data.job) return;

        const { status, error } = data.job;

        if (status === "succeeded") {
          addLine("[info] Job completed successfully. Service will restart...");
          stopJobPolling();
          // Close SSE immediately — don't wait for the browser to notice the
          // disconnect. The service is about to restart; start recovery now.
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          if (sseSilenceTimerRef.current) {
            clearTimeout(sseSilenceTimerRef.current);
            sseSilenceTimerRef.current = null;
          }
          if (phaseRef.current === "streaming") {
            startRecoveryPolling();
          }
        } else if (status === "failed") {
          stopJobPolling();
          // Close SSE — no point streaming after a failure
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          if (sseSilenceTimerRef.current) {
            clearTimeout(sseSilenceTimerRef.current);
            sseSilenceTimerRef.current = null;
          }
          addLine(`[error] Job failed: ${error ?? "Unknown error"}`);
          setError(error ?? "Update job failed");
        }
      } catch {
        // Ignore polling errors - server might be restarting
      }
    };

    jobPollTimerRef.current = setInterval(() => void pollJob(), JOB_POLL_INTERVAL);
    // Also poll immediately
    void pollJob();
  }, [addLine, setError, stopJobPolling, startRecoveryPolling]);

  // ── SSE stream connection ─────────────────────────────────────────────

  const connectStream = useCallback(() => {
    setPhase("streaming");

    const es = new EventSource(STREAM_URL, { withCredentials: true });
    eventSourceRef.current = es;

    // Start job status polling in parallel
    startJobPolling();

    // ── Silence timeout (Issue 2 fix) ────────────────────────────────
    // Track last event time. If nothing arrives for SSE_SILENCE_TIMEOUT_MS
    // while in streaming phase, the connection is stale — switch to recovery.
    const resetSilenceTimer = () => {
      if (sseSilenceTimerRef.current) clearTimeout(sseSilenceTimerRef.current);
      sseSilenceTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        if (phaseRef.current !== "streaming") return;
        addLine("[info] No output received for 30s. Checking service status...");
        es.close();
        eventSourceRef.current = null;
        stopJobPolling();
        startRecoveryPolling();
      }, SSE_SILENCE_TIMEOUT_MS);
    };
    // Start the initial silence timer
    resetSilenceTimer();

    es.addEventListener("line", (event: MessageEvent) => {
      resetSilenceTimer();
      try {
        const parsed = JSON.parse(event.data as string) as { text: string };
        addLine(parsed.text);
      } catch { /* ignore parse errors */ }
    });

    es.addEventListener("init", () => {
      // Stream connected — reset silence timer
      resetSilenceTimer();
    });

    // ── SSE error handler (Issue 3 fix) ──────────────────────────────
    // During an active update, ANY SSE disconnect is the expected signal
    // that the service is stopping for restart. No tolerance — immediately
    // close and switch to recovery polling.
    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      stopJobPolling();
      if (sseSilenceTimerRef.current) {
        clearTimeout(sseSilenceTimerRef.current);
        sseSilenceTimerRef.current = null;
      }

      // Stream disconnected -- expected during service restart
      if (phaseRef.current === "streaming") {
        startRecoveryPolling();
      }
    };
  }, [addLine, setPhase, startRecoveryPolling, startJobPolling, stopJobPolling]);

  // ── Trigger update ────────────────────────────────────────────────────

  const startUpdate = useCallback(async () => {
    setState({ phase: "starting", lines: [], error: null, newCommit: null, newVersion: null, isReinstall: false, escalatedJobId: null });
    phaseRef.current = "starting";

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (csrfToken) headers["x-csrf-token"] = csrfToken;

      const res = await fetch(TRIGGER_URL, {
        method: "POST",
        headers,
        body: "{}",
        credentials: "include",
      });

      const contentType = res.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) {
        const text = await res.text();
        const preview = text.slice(0, 200).replace(/<[^>]*>/g, "").trim();
        setError(`Non-JSON response from server (HTTP ${res.status}). ${preview || "The server or proxy may have timed out."}`);
        return;
      }

      const data = await res.json() as Record<string, unknown>;

      // Handle both 200 (legacy) and 202 (new non-blocking)
      const isAccepted = res.status === 202 || res.status === 200;
      if (!isAccepted || data.ok !== true) {
        const errMsg = (data.error as Record<string, unknown> | undefined)?.message ??
          data.message ?? "Failed to start update.";
        setError(String(errMsg));
        return;
      }

      if (!mountedRef.current) return;

      // CLI-core-aware escalation: when the CLI core is also behind, the backend
      // dispatched a full `qb update quickbox` job instead of the dashboard-only
      // helper. That job is already running, so we do NOT connect the
      // dashboard-update stream here - we surface the maintenance jobId and let
      // the caller attach the maintenance modal to it (core-first, then dashboard).
      if (data.escalatedToFullUpdate === true) {
        const escalatedJobId = typeof data.jobId === "string" ? data.jobId : "";
        setState((prev) => ({ ...prev, escalatedJobId }));
        return;
      }

      // Extract jobId if present (202 response)
      const jobId = data.jobId as string | undefined;
      jobIdRef.current = jobId ?? null;
      if (jobId) {
        addLine(`[info] Update accepted (job: ${jobId}). Connecting to output stream...`);
      } else {
        addLine("[info] Update triggered. Connecting to output stream...");
      }

      // Connect SSE stream
      connectStream();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error");
    }
  }, [addLine, setError, connectStream]);

  // ── Reset ─────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
    if (sseSilenceTimerRef.current) clearTimeout(sseSilenceTimerRef.current);
    sseSilenceTimerRef.current = null;
    stopJobPolling();
    pollAttemptRef.current = 0;
    jobIdRef.current = null;
    setState({ phase: "idle", lines: [], error: null, newCommit: null, newVersion: null, isReinstall: false, escalatedJobId: null });
    phaseRef.current = "idle";
  }, [stopJobPolling]);

  return {
    phase: state.phase,
    lines: state.lines,
    error: state.error,
    newCommit: state.newCommit,
    newVersion: state.newVersion,
    isReinstall: state.isReinstall,
    escalatedJobId: state.escalatedJobId,
    startUpdate,
    reset,
  };
};
